import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";

export interface ServiceItem {
  id: string;
  slug: string;
  title: { ar: string; en: string };
  description: { ar: string; en: string };
  image: string;
}

export function stripHtml(html: string) {
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function useInView(ref: React.RefObject<HTMLElement>) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setVisible(true),
      { threshold: 0.12 },
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return visible;
}

/* ─── Service Row: full-width grid, image | content ─── */
export function ServiceRow({
  service,
  idx,
  isAr,
  onClick,
}: {
  service: ServiceItem;
  idx: number;
  isAr: boolean;
  onClick: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null!);
  const visible = useInView(ref);
  const [hovered, setHovered] = useState(false);

  const plain = stripHtml(
    isAr ? service.description.ar : service.description.en,
  );
  const preview = plain.length > 150 ? plain.slice(0, 150) + "…" : plain;

  /* odd rows: image on right, even: image on left (flips for RTL too) */
  const imageOnRight = isAr ? idx % 2 === 0 : idx % 2 !== 0;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 56 }}
      animate={visible ? { opacity: 1, y: 0 } : {}}
      transition={{
        duration: 0.7,
        delay: idx * 0.05,
        ease: [0.22, 1, 0.36, 1],
      }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      onClick={onClick}
      style={{
        display: "grid",
        gridTemplateColumns: imageOnRight ? "1fr 480px" : "480px 1fr",
        minHeight: 420,
        background: "#FFFFFF",
        borderRadius: 24,
        overflow: "hidden",
        cursor: "pointer",
        border: "1px solid",
        borderColor: hovered ? "#0A0A14" : "#EAEAF0",
        boxShadow: hovered
          ? "0 32px 80px -16px rgba(10,10,20,0.18)"
          : "0 1px 3px rgba(10,10,20,0.05)",
        transition: "border-color 0.3s ease, box-shadow 0.4s ease",
      }}
    >
      {/* ── Image side ── */}
      <div
        style={{
          position: "relative",
          overflow: "hidden",
          order: imageOnRight ? 2 : 1,
          borderLeft: imageOnRight ? "1px solid" : "none",
          borderRight: imageOnRight ? "none" : "1px solid",
          borderColor: hovered ? "#0A0A14" : "#EAEAF0",
          transition: "border-color 0.3s ease",
        }}
      >
        {/* image */}
        <Image
          src={service.image}
          alt={isAr ? service.title.ar : service.title.en}
          fill
          style={{
            objectFit: "cover",
            transform: hovered ? "scale(1.05)" : "scale(1)",
            transition: "transform 0.8s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        />

        {/* dark gradient overlay */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: imageOnRight
              ? "linear-gradient(to right, rgba(255,255,255,0.18) 0%, transparent 50%)"
              : "linear-gradient(to left, rgba(255,255,255,0.18) 0%, transparent 50%)",
            transition: "opacity 0.3s",
          }}
        />

        {/* index stamp */}
        <div
          style={{
            position: "absolute",
            bottom: 24,
            left: imageOnRight ? "auto" : 24,
            right: imageOnRight ? 24 : "auto",
            fontFamily: "'DM Mono', monospace",
            fontSize: 72,
            fontWeight: 500,
            lineHeight: 1,
            color: "rgba(255,255,255,0.15)",
            letterSpacing: "-0.05em",
            userSelect: "none",
            pointerEvents: "none",
          }}
        >
          {String(idx + 1).padStart(2, "0")}
        </div>
      </div>

      {/* ── Content side ── */}
      <div
        style={{
          order: imageOnRight ? 1 : 2,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "56px 64px",
          textAlign: isAr ? "right" : "left",
          gap: 0,
        }}
      >
        {/* eyebrow */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 20,
            flexDirection: isAr ? "row-reverse" : "row",
          }}
        >
          <div
            style={{
              width: 28,
              height: 1,
              background: hovered ? "#0A0A14" : "#C0C0D0",
              transition: "background 0.3s",
            }}
          />
          <span
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 10,
              letterSpacing: "0.25em",
              textTransform: "uppercase",
              color: hovered ? "#0A0A14" : "#9999B0",
              transition: "color 0.3s",
              fontWeight: 500,
            }}
          >
            {isAr ? "خدمة" : "Service"} · {String(idx + 1).padStart(2, "0")}
          </span>
        </div>

        {/* title */}
        <h3
          style={{
            fontFamily: "'Fraunces', serif",
            fontSize: "clamp(1.6rem, 2.4vw, 2.2rem)",
            fontWeight: 700,
            color: "#0A0A14",
            lineHeight: 1.15,
            letterSpacing: "-0.03em",
            marginBottom: 20,
          }}
        >
          {isAr ? service.title.ar : service.title.en}
        </h3>

        {/* divider */}
        <div
          style={{
            width: 40,
            height: 2,
            background: "#0A0A14",
            borderRadius: 99,
            marginBottom: 20,
            alignSelf: isAr ? "flex-end" : "flex-start",
            opacity: hovered ? 1 : 0.2,
            transition: "opacity 0.4s",
          }}
        />

        {/* description */}
        <p
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 15,
            color: "#6A6A80",
            lineHeight: 1.75,
            marginBottom: 40,
            maxWidth: 380,
            alignSelf: isAr ? "flex-end" : "flex-start",
          }}
        >
          {preview}
        </p>

        {/* CTA button */}
        <div style={{ alignSelf: isAr ? "flex-end" : "flex-start" }}>
          <motion.div
            animate={hovered ? { y: -2 } : { y: 0 }}
            transition={{ duration: 0.25 }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              background: hovered ? "#0A0A14" : "#F2F2F8",
              color: hovered ? "#FFFFFF" : "#0A0A14",
              borderRadius: 99,
              padding: "14px 26px",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 14,
              fontWeight: 600,
              transition: "background 0.3s, color 0.3s",
              letterSpacing: "-0.01em",
            }}
          >
            {isAr ? "تفاصيل الخدمة" : "View Service"}
            <ArrowUpRight
              size={16}
              style={{ transform: isAr ? "scaleX(-1)" : "none" }}
            />
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
